import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";

const GRID_SIZE = 15;
const DIRECTIONS = [
  {dr:0,dc:1},{dr:0,dc:-1},{dr:1,dc:0},{dr:-1,dc:0},
  {dr:1,dc:1},{dr:1,dc:-1},{dr:-1,dc:1},{dr:-1,dc:-1},
];

function normalizeWord(str) {
  if (!str) return "";
  return str.toUpperCase().replace(/[^A-ZÀ-ÖØ-Ý]/g,"").normalize("NFD").replace(/[\u0300-\u036f]/g,"");
}

// iOS-style accent colours (no glow, clean fills)
const FOUND_COLORS = [
  "#0a84ff","#30d158","#bf5af2","#ff9f0a",
  "#ff453a","#64d2ff","#5e5ce6","#ff375f",
  "#34c759","#007aff",
];

const C = {
  bg: "#0a0a0f", surface: "rgba(255,255,255,0.04)", sh: "rgba(255,255,255,0.07)",
  border: "rgba(255,255,255,0.08)", text: "#f5f5f7",
  sec: "rgba(245,245,247,0.55)", ter: "rgba(245,245,247,0.30)",
  accent: "#0a84ff", green: "#30d158",
};
const F = "-apple-system,'SF Pro Display','Helvetica Neue',sans-serif";
const CELL = 20;

export default function Crucipuzzle() {
  const { localeTag, tableId } = useParams();
  const navigate = useNavigate();
  const tokenUrl = new URLSearchParams(useLocation().search).get("token");
  const normalizedLocaleTag = (localeTag || "").toLowerCase();

  const [loading, setLoading]   = useState(true);
  const [grid, setGrid]         = useState(Array.from({length:GRID_SIZE},()=>Array(GRID_SIZE).fill("")));
  const [words, setWords]       = useState([]);
  const [error, setError]       = useState("");
  const [selectedStart, setSelectedStart] = useState(null);
  const [selectedEnd, setSelectedEnd]     = useState(null);
  const [showInstr, setShowInstr] = useState(false);
  const [newlyFound, setNewlyFound] = useState(null);
  const [tokenScaduto, setTS]   = useState(false);
  const [tokenLoading, setTL]   = useState(true);

  const isDragging = useRef(false);

  const foundCoords = useMemo(() => {
    const map = {};
    words.filter(w => w.found && w.path).forEach((w, wi) => {
      w.path.forEach(({r,c}) => { map[`${r}-${c}`] = wi % FOUND_COLORS.length; });
    });
    return map;
  }, [words]);

  const previewPath = useMemo(() => {
    if (!selectedStart || !selectedEnd) return new Set();
    const {r:sr,c:sc} = selectedStart, {r:er,c:ec} = selectedEnd;
    const dr = er-sr, dc = ec-sc;
    const steps = Math.max(Math.abs(dr),Math.abs(dc));
    if (steps === 0) return new Set();
    const stepR = dr===0?0:dr/Math.abs(dr), stepC = dc===0?0:dc/Math.abs(dc);
    if (!(stepR===0||stepC===0||Math.abs(dr)===Math.abs(dc))) return new Set();
    const set = new Set();
    for (let i=0;i<=steps;i++) set.add(`${sr+stepR*i}-${sc+stepC*i}`);
    return set;
  }, [selectedStart, selectedEnd]);

  // ── Token ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const check = async () => {
      try {
        if (!tokenUrl) { setTS(true); setTL(false); return; }
        const snap = await getDoc(doc(db,"locali",normalizedLocaleTag,"tavoli",tableId));
        if (!snap.exists()) { setTS(true); setTL(false); return; }
        const {tokenAttuale} = snap.data()||{};
        if (!tokenAttuale||tokenUrl!==tokenAttuale) setTS(true);
        setTL(false);
      } catch { setTS(true); setTL(false); }
    };
    check();
  }, [tokenUrl, normalizedLocaleTag, tableId]);

  // ── Words ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (tokenScaduto) return;
    const load = async () => {
      try {
        setLoading(true);
        const q = query(collection(db,"menu"),where("localeTag","==",localeTag));
        const snap = await getDocs(q);
        const raw = [];
        snap.forEach(d => { if (d.data()?.nome) raw.push(d.data().nome); });
        let cleaned = raw.map(normalizeWord).filter(w=>w.length>=3&&w.length<=12);
        if (!cleaned.length) cleaned = ["PIZZA","PASTA","RISOTTO","DOLCE","CAFFE","BRUSCHETTA","INSALATA"];
        cleaned = shuffleArray(cleaned).slice(0,10);
        const {grid:g, placedWords} = generateGrid(cleaned);
        setGrid(g); setWords(placedWords); setError("");
      } catch { setError("Errore nel caricamento del crucipuzzle."); }
      finally { setLoading(false); }
    };
    load();
  }, [localeTag, tokenScaduto]);

  // ── Conditional returns ─────────────────────────────────────────────────────
  if (tokenLoading) return (
    <div style={{ minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:C.bg,fontFamily:F }}>
      <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:"1rem" }}>
        <div style={{ width:32,height:32,border:`2px solid ${C.border}`,borderTop:`2px solid ${C.sec}`,borderRadius:"50%",animation:"spin 0.8s linear infinite" }} />
        <p style={{ margin:0,fontSize:"0.82rem",color:C.sec }}>Caricamento…</p>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (tokenScaduto) return (
    <div style={{ minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:C.bg,padding:"2rem",fontFamily:F }}>
      <div style={{ background:"rgba(255,255,255,0.05)",border:`1px solid ${C.border}`,borderRadius:24,padding:"2.5rem 2rem",maxWidth:340,width:"100%",textAlign:"center" }}>
        <div style={{ fontSize:"2.5rem",marginBottom:"1rem" }}>⏱</div>
        <h2 style={{ margin:"0 0 0.5rem",fontSize:"1.1rem",fontWeight:700,color:C.text }}>Sessione scaduta</h2>
        <p style={{ margin:0,fontSize:"0.85rem",color:C.sec,lineHeight:1.6 }}>Scansiona il QR del tavolo per iniziare una nuova sessione.</p>
      </div>
    </div>
  );

  // ── Game logic (unchanged) ──────────────────────────────────────────────────
  const handleCellPointerDown = (r,c) => { isDragging.current=true; setSelectedStart({r,c}); setSelectedEnd({r,c}); };
  const handleCellPointerEnter = (r,c) => { if (!isDragging.current) return; setSelectedEnd({r,c}); };
  const handleCellPointerUp = (r,c) => {
    if (!isDragging.current) return; isDragging.current=false;
    if (!selectedStart) return;
    const end={r,c};
    if (selectedStart.r===end.r&&selectedStart.c===end.c) { setSelectedStart(null); setSelectedEnd(null); return; }
    checkSelection(selectedStart,end);
  };
  const handleCellClick = (r,c) => {
    if (selectedStart===null) { setSelectedStart({r,c}); setSelectedEnd(null); return; }
    if (selectedStart.r===r&&selectedStart.c===c) { setSelectedStart(null); setSelectedEnd(null); return; }
    checkSelection(selectedStart,{r,c});
  };
  const checkSelection = (start,end) => {
    if (!start||!end) return;
    const dr=end.r-start.r, dc=end.c-start.c;
    const steps=Math.max(Math.abs(dr),Math.abs(dc));
    if (steps===0) return;
    const stepR=dr===0?0:dr/Math.abs(dr), stepC=dc===0?0:dc/Math.abs(dc);
    if (!(stepR===0||stepC===0||Math.abs(dr)===Math.abs(dc))) { setSelectedStart(null); setSelectedEnd(null); return; }
    const path=[]; let word="", r=start.r, c=start.c;
    for (let i=0;i<=steps;i++) { word+=grid[r][c]; path.push({r,c}); r+=stepR; c+=stepC; }
    const rev=word.split("").reverse().join("");
    let found=false;
    setWords(prev=>prev.map(w=>{
      if (!w.found&&(w.text===word||w.text===rev)) { found=true; return {...w,found:true,path}; }
      return w;
    }));
    if (found) { setNewlyFound(word.length>rev.length?word:rev); setTimeout(()=>setNewlyFound(null),1200); }
    setSelectedStart(null); setSelectedEnd(null);
  };

  const allFound = words.length>0&&words.every(w=>w.found);
  const foundCount = words.filter(w=>w.found).length;

  return (
    <div style={{ minHeight:"100vh",background:C.bg,display:"flex",justifyContent:"center",alignItems:"flex-start",padding:"1.5rem 1rem 3rem",fontFamily:F,color:C.text,overflowX:"hidden" }}>
      <div style={{ maxWidth:520,width:"100%" }}>

        {/* Header */}
        <div style={{ marginBottom:"1.5rem" }}>
          <p style={{ margin:"0 0 0.2rem",fontSize:"0.72rem",color:C.sec,fontWeight:500 }}>Giochi del tavolo</p>
          <h1 style={{ margin:"0 0 0.2rem",fontSize:"1.5rem",fontWeight:700,letterSpacing:"-0.5px" }}>Crucipuzzle</h1>
          <p style={{ margin:0,fontSize:"0.72rem",color:C.ter }}>Tavolo {tableId} · {localeTag}</p>
        </div>

        {/* Progress */}
        {!loading && words.length>0 && (
          <div style={{ marginBottom:"1.25rem" }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.4rem" }}>
              <span style={{ fontSize:"0.7rem",color:C.sec,fontWeight:500 }}>Parole trovate</span>
              <span style={{ fontSize:"0.7rem",fontWeight:700,color:C.green }}>{foundCount} / {words.length}</span>
            </div>
            <div style={{ height:4,borderRadius:99,background:C.border,overflow:"hidden" }}>
              <div style={{ height:"100%",borderRadius:99,width:`${(foundCount/words.length)*100}%`,background:C.green,transition:"width 0.5s ease" }} />
            </div>
          </div>
        )}

        {error && <div style={{ padding:"0.75rem 1rem",background:"rgba(255,69,58,0.1)",border:"1px solid rgba(255,69,58,0.25)",borderRadius:12,fontSize:"0.8rem",color:"#ff453a",marginBottom:"1rem" }}>{error}</div>}

        {loading ? (
          <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:"1rem",padding:"3rem 0" }}>
            <div style={{ width:32,height:32,border:`2px solid ${C.border}`,borderTop:`2px solid ${C.sec}`,borderRadius:"50%",animation:"spin 0.8s linear infinite" }} />
            <p style={{ margin:0,fontSize:"0.82rem",color:C.sec }}>Generazione puzzle…</p>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : (
          <>
            {/* Instructions */}
            <button onClick={() => setShowInstr(v=>!v)} style={{ width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"0.7rem 0.9rem",background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,color:C.sec,fontSize:"0.78rem",fontWeight:500,cursor:"pointer",marginBottom:"0.75rem",fontFamily:F,transition:"background 150ms ease" }}
              onMouseEnter={e=>e.currentTarget.style.background=C.sh} onMouseLeave={e=>e.currentTarget.style.background=C.surface}>
              <span>Come si gioca</span>
              <span style={{ fontSize:"0.7rem" }}>{showInstr?"▲":"▼"}</span>
            </button>
            {showInstr && (
              <div style={{ padding:"0.9rem 1rem",background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,marginBottom:"0.75rem",fontSize:"0.75rem",color:C.sec,lineHeight:1.8 }}>
                <p style={{ margin:"0 0 0.3rem" }}>1. Tocca la <strong style={{color:C.text}}>prima lettera</strong> della parola</p>
                <p style={{ margin:"0 0 0.3rem" }}>2. Trascina o tocca l'<strong style={{color:C.text}}>ultima lettera</strong></p>
                <p style={{ margin:"0 0 0.3rem" }}>3. Le parole trovate si colorano</p>
                <p style={{ margin:0,color:C.ter }}>Orizzontali · Verticali · Diagonali · Anche al contrario</p>
              </div>
            )}

            {/* Found flash */}
            {newlyFound && (
              <div style={{ textAlign:"center",padding:"0.6rem",background:"rgba(48,209,88,0.1)",border:"1px solid rgba(48,209,88,0.25)",borderRadius:10,fontSize:"0.82rem",fontWeight:600,color:C.green,marginBottom:"0.75rem" }}>
                ✓ {newlyFound} trovata!
              </div>
            )}

            {/* Grid */}
            <div style={{ display:"grid",gridTemplateColumns:`repeat(${GRID_SIZE},${CELL}px)`,gap:2,justifyContent:"center",marginBottom:"1.25rem",padding:"0.9rem",background:C.surface,borderRadius:16,border:`1px solid ${C.border}`,touchAction:"none",userSelect:"none",overflowX:"auto" }}
              onMouseLeave={() => { if (isDragging.current) { isDragging.current=false; setSelectedStart(null); setSelectedEnd(null); } }}
            >
              {grid.map((row,r)=>row.map((cell,c) => {
                const key=`${r}-${c}`;
                const ci = foundCoords[key];
                const isFound = ci!==undefined;
                const isPreview = previewPath.has(key);
                const isStart = selectedStart?.r===r&&selectedStart?.c===c;
                const accent = isFound ? FOUND_COLORS[ci] : null;

                return (
                  <button key={key}
                    onPointerDown={() => handleCellPointerDown(r,c)}
                    onPointerEnter={() => handleCellPointerEnter(r,c)}
                    onPointerUp={() => handleCellPointerUp(r,c)}
                    onClick={() => !isDragging.current&&handleCellClick(r,c)}
                    style={{
                      width:CELL,height:CELL,
                      fontSize:"0.52rem",fontWeight:isFound?700:500,
                      borderRadius:5,
                      border:`1px solid ${isFound?accent+"44":isPreview?"rgba(10,132,255,0.3)":"rgba(255,255,255,0.06)"}`,
                      padding:0,lineHeight:1,cursor:"pointer",
                      backgroundColor: isFound ? accent+"22" : isStart ? "rgba(10,132,255,0.25)" : isPreview ? "rgba(10,132,255,0.12)" : "rgba(255,255,255,0.04)",
                      color: isFound ? accent : isStart||isPreview ? "#0a84ff" : "rgba(245,245,247,0.75)",
                      transform: isStart?"scale(1.12)":"scale(1)",
                      transition:"background 120ms ease,transform 100ms ease",
                      fontFamily:F,
                    }}
                  >{cell}</button>
                );
              }))}
            </div>

            {/* Word chips */}
            <div style={{ background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:"1rem",marginBottom:"1rem" }}>
              <p style={{ margin:"0 0 0.7rem",fontSize:"0.65rem",color:C.sec,fontWeight:600,letterSpacing:"0.3px" }}>PAROLE DA TROVARE</p>
              <div style={{ display:"flex",flexWrap:"wrap",gap:"0.4rem" }}>
                {words.map((w,idx) => {
                  const color = FOUND_COLORS[idx%FOUND_COLORS.length];
                  return (
                    <span key={idx} style={{
                      fontSize:"0.72rem",fontWeight:w.found?600:500,
                      padding:"0.3rem 0.65rem",borderRadius:999,
                      backgroundColor: w.found ? color+"22" : "rgba(255,255,255,0.05)",
                      border: `1px solid ${w.found ? color+"44" : "rgba(255,255,255,0.1)"}`,
                      color: w.found ? color : C.sec,
                      textDecoration: w.found ? "line-through" : "none",
                      transition:"all 0.25s ease",
                    }}>
                      {w.found&&"✓ "}{w.text}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Win */}
            {allFound && (
              <div style={{ padding:"1rem",background:"rgba(48,209,88,0.1)",border:"1px solid rgba(48,209,88,0.25)",borderRadius:16,marginBottom:"1rem",textAlign:"center" }}>
                <p style={{ margin:"0 0 0.2rem",fontSize:"1rem",fontWeight:700,color:C.green }}>🏆 Complimenti!</p>
                <p style={{ margin:0,fontSize:"0.8rem",color:C.sec }}>Hai trovato tutte le parole del menù</p>
              </div>
            )}
          </>
        )}

        <button onClick={() => navigate(`/giochi/${localeTag}/${tableId}?token=${tokenUrl}`)} style={{ width:"100%",padding:"0.7rem",background:"transparent",border:`1px solid ${C.border}`,borderRadius:12,color:C.sec,fontSize:"0.82rem",fontWeight:500,cursor:"pointer",fontFamily:F,transition:"background 150ms ease" }}
          onMouseEnter={e=>e.currentTarget.style.background=C.surface} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          ← Torna ai giochi
        </button>

      </div>
    </div>
  );
}

// ── Game helpers (unchanged) ───────────────────────────────────────────────────
function shuffleArray(arr) {
  const a=[...arr];
  for (let i=a.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[a[i],a[j]]=[a[j],a[i]];}
  return a;
}
function generateGrid(wordList){
  const grid=Array.from({length:GRID_SIZE},()=>Array(GRID_SIZE).fill(""));
  const placed=[];
  for (const word of wordList){
    const p=placeWordInGrid(grid,word.toUpperCase());
    if (p) placed.push({text:word.toUpperCase(),...p,found:false});
  }
  for (let r=0;r<GRID_SIZE;r++) for (let c=0;c<GRID_SIZE;c++) if (!grid[r][c]) grid[r][c]=randomLetter();
  return {grid,placedWords:placed};
}
function placeWordInGrid(grid,word){
  const dirs=[{dr:0,dc:1},{dr:0,dc:-1},{dr:1,dc:0},{dr:-1,dc:0},{dr:1,dc:1},{dr:1,dc:-1},{dr:-1,dc:1},{dr:-1,dc:-1}];
  for (let attempt=0;attempt<200;attempt++){
    const {dr,dc}=dirs[Math.floor(Math.random()*dirs.length)];
    const maxRow=dr===1?GRID_SIZE-word.length:dr===-1?GRID_SIZE-1:GRID_SIZE-1;
    const minRow=dr===-1?word.length-1:0;
    const maxCol=dc===1?GRID_SIZE-word.length:dc===-1?GRID_SIZE-1:GRID_SIZE-1;
    const minCol=dc===-1?word.length-1:0;
    const sr=minRow+Math.floor(Math.random()*(maxRow-minRow+1));
    const sc=minCol+Math.floor(Math.random()*(maxCol-minCol+1));
    let r=sr,c=sc,fits=true;
    for (let i=0;i<word.length;i++){
      if (r<0||r>=GRID_SIZE||c<0||c>=GRID_SIZE||(grid[r][c]&&grid[r][c]!==word[i])){fits=false;break;}
      r+=dr;c+=dc;
    }
    if (!fits) continue;
    const path=[];r=sr;c=sc;
    for (let i=0;i<word.length;i++){grid[r][c]=word[i];path.push({r,c});r+=dr;c+=dc;}
    return {start:{r:sr,c:sc},end:{r:r-dr,c:c-dc},path};
  }
  return null;
}
function randomLetter(){return"ABCDEFGHIJKLMNOPQRSTUVWXYZ"[Math.floor(Math.random()*26)];}
